// @ts-strict-ignore

import { Vector3 } from 'three';

const EPSILON = 0.0005;
const CommonConstantsEPSILON = 0.08;

class Line {
  s: Vector3;
  e: Vector3;
  count: number = 0;
  lastHasNumber3DResult: number = 0;

  private equalsXNumber3D(fPoint: Vector3, sPoint: Vector3, epsilon: number = NaN): boolean {
    return Math.abs(fPoint.x - sPoint.x) < (isNaN(epsilon) ? CommonConstantsEPSILON : epsilon);
  }

  public equalsYNumber3D(fPoint: Vector3, sPoint: Vector3, epsilon: number = NaN): boolean {
    return Math.abs(fPoint.y - sPoint.y) < (isNaN(epsilon) ? CommonConstantsEPSILON : epsilon);
  }

  public equalsZNumber3D(fPoint: Vector3, sPoint: Vector3, epsilon: number = NaN): boolean {
    return Math.abs(fPoint.z - sPoint.z) < (isNaN(epsilon) ? CommonConstantsEPSILON : epsilon);
  }

  public equalsXYZNumber3D(fPoint: Vector3, sPoint: Vector3, epsilon: number = NaN): boolean {
    return (
      this.equalsXNumber3D(fPoint, sPoint, epsilon) && this.equalsYNumber3D(fPoint, sPoint, epsilon) && this.equalsZNumber3D(fPoint, sPoint, epsilon)
    );
  }

  hasNumber3D(v: Vector3): number {
    this.lastHasNumber3DResult = 0;
    if (this.equalsXYZNumber3D(this.s, v, EPSILON)) {
      this.lastHasNumber3DResult = 1;
    } else if (this.equalsXYZNumber3D(this.e, v, EPSILON)) {
      this.lastHasNumber3DResult = -1;
    }
    return this.lastHasNumber3DResult;
  }

  equals(line: Line): boolean {
    return this.equalsXYZNumber3D(this.s, line.s, EPSILON) && this.equalsXYZNumber3D(this.e, line.e, EPSILON);
  }
}

export class BoundingContour {
  private _lines;

  private _points: Vector3[];

  private _vertices;

  constructor(vertices, faces, closed: boolean = true) {
    this._vertices = vertices;
    this.collectLines(faces);
    this.calculate();
  }

  private collectLines(_faces): void {
    this._lines = [];
    for (let i = 0; i < _faces.length; i++) {
      const l1 = this.getLine(this._vertices[_faces[i].a], this._vertices[_faces[i].b]);
      this.addLine(l1);
      const l2 = this.getLine(this._vertices[_faces[i].b], this._vertices[_faces[i].c]);
      this.addLine(l2);
      const l3 = this.getLine(this._vertices[_faces[i].c], this._vertices[_faces[i].a]);
      this.addLine(l3);
    }
  }

  private addLine(line: Line): void {
    for (let i = this._lines.length - 1; i > -1; i--) {
      const eline = this._lines[i];
      if (eline.equals(line)) {
        this._lines.splice(i, 1);
        return;
      }
    }
    this._lines.push(line);
  }

  private calculate(): void {
    const notUsedLines = this._lines;
    this._points = [];
    if (notUsedLines == null || notUsedLines.length === 0) {
      return;
    }
    const line = notUsedLines[0];
    this._points.push(line.s);
    this._points.push(line.e);
    notUsedLines.splice(0, 1);
    let checkPoint = line.e;
    while (notUsedLines.length > 0) {
      const neighborLineIndex = this.findLine(checkPoint, notUsedLines);
      if (neighborLineIndex === -1) {
        break;
      }
      const neighborLine = notUsedLines[neighborLineIndex];
      if (neighborLine.lastHasNumber3DResult === 1) {
        this._points.push(neighborLine.e);
        checkPoint = neighborLine.e;
      } else {
        this._points.push(neighborLine.s);
        checkPoint = neighborLine.s;
      }
      notUsedLines.splice(notUsedLines.indexOf(neighborLine), 1);
    }
    if (this._points.length > 0 && !this.equalsXYZNumber3D(this._points[0], this._points[this._points.length - 1], EPSILON)) {
      this._points.push(this._points[0]);
    }
    this.removeUnnecessaryIntermediatePoints(this._points);
  }

  private equalsXNumber3D(fPoint: Vector3, sPoint: Vector3, epsilon: number = NaN): boolean {
    return Math.abs(fPoint.x - sPoint.x) < (isNaN(epsilon) ? CommonConstantsEPSILON : epsilon);
  }

  private equalsXNumber3Ds(epsilon: number, points: any[]): boolean {
    if (points == null || points.length === 0) {
      return true;
    }
    let previousPoint = null;
    let i = 0;
    while (i < points.length) {
      const currentPoint = points[i];
      if (previousPoint != null) {
        if (!this.equalsXNumber3D(previousPoint, currentPoint, epsilon)) {
          return false;
        }
      }
      previousPoint = currentPoint;
      i++;
    }
    return true;
  }

  public equalsYNumber3D(fPoint: Vector3, sPoint: Vector3, epsilon: number = NaN): boolean {
    return Math.abs(fPoint.y - sPoint.y) < (isNaN(epsilon) ? CommonConstantsEPSILON : epsilon);
  }

  public equalsYNumber3Ds(epsilon: number, points: any[]): boolean {
    if (points == null || points.length === 0) {
      return true;
    }
    let previousPoint = null;
    let i = 0;
    while (i < points.length) {
      const currentPoint = points[i];
      if (previousPoint != null) {
        if (!this.equalsYNumber3D(previousPoint, currentPoint, epsilon)) {
          return false;
        }
      }
      previousPoint = currentPoint;
      i++;
    }
    return true;
  }

  public equalsZNumber3D(fPoint: Vector3, sPoint: Vector3, epsilon: number = NaN): boolean {
    return Math.abs(fPoint.z - sPoint.z) < (isNaN(epsilon) ? CommonConstantsEPSILON : epsilon);
  }

  public equalsZNumber3Ds(epsilon: number, points: any[]): boolean {
    if (points == null || points.length === 0) {
      return true;
    }
    let previousPoint = null;
    let i = 0;
    while (i < points.length) {
      const currentPoint = points[i];
      if (previousPoint != null) {
        if (!this.equalsZNumber3D(previousPoint, currentPoint, epsilon)) {
          return false;
        }
      }
      previousPoint = currentPoint;
      i++;
    }
    return true;
  }

  public equalsXYZNumber3D(fPoint: Vector3, sPoint: Vector3, epsilon: number = NaN): boolean {
    return (
      this.equalsXNumber3D(fPoint, sPoint, epsilon) && this.equalsYNumber3D(fPoint, sPoint, epsilon) && this.equalsZNumber3D(fPoint, sPoint, epsilon)
    );
  }

  private removeUnnecessaryIntermediatePoints(points: Vector3[]): void {
    if (points == null || points.length === 0) {
      return;
    }
    let i = 0;
    let firstPoint = null;
    let middlePoint = null;
    while (i < points.length) {
      const currentPoint = points[i];
      let middlePointRemoved = false;
      if (firstPoint != null && middlePoint != null) {
        const array = [];
        array.push(firstPoint);
        array.push(currentPoint);
        array.push(middlePoint);
        const xEqual = this.equalsXNumber3Ds(EPSILON, array);
        const yEqual = this.equalsYNumber3Ds(EPSILON, array);
        const zEqual = this.equalsZNumber3Ds(EPSILON, array);
        if ((xEqual && yEqual) || (yEqual && zEqual) || (zEqual && xEqual)) {
          points.splice(i - 1, 1);
          middlePointRemoved = true;
        }
      }
      if (middlePointRemoved) {
        middlePoint = currentPoint;
      } else {
        firstPoint = middlePoint;
        middlePoint = currentPoint;
        i++;
      }
    }
  }

  private findLine(point: Vector3, lines): number {
    for (let i = 0; i < lines.length; i++) {
      const line: Line = lines[i];
      if (line.hasNumber3D(point) !== 0) {
        return i;
      }
    }
    return -1;
  }

  private getLine(s: Vector3, e: Vector3): Line {
    const l = new Line();
    let checkCoordinateS: Number;
    let checkCoordinateE: Number;
    if (!this.equalsXNumber3D(s, e, EPSILON)) {
      checkCoordinateS = s.x;
      checkCoordinateE = e.x;
    } else if (!this.equalsYNumber3D(s, e, EPSILON)) {
      checkCoordinateS = s.y;
      checkCoordinateE = e.y;
    } else {
      checkCoordinateS = s.z;
      checkCoordinateE = e.z;
    }
    if (checkCoordinateS < checkCoordinateE) {
      l.s = s;
      l.e = e;
    } else {
      l.s = e;
      l.e = s;
    }
    return l;
  }

  public getPoints(): Vector3[] {
    return this._points;
  }
}
